import { useState, useEffect } from "react";
import { db } from "../../firebase/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp
} from "firebase/firestore";
import { MessageInterface } from "./dto";

interface ChatRoom {
  sender: string;
  jobId: string;
  status: string;
  recipient: string;
  jobTitle: string;
  companyName: string;
  messages: MessageInterface[];
  hasMoreMessages: boolean;
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  timestamp: any;
}

const useChatRooms = (userUid: string) => {
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const messagesQuery = query(
      collection(db, "messages"),
      where("participants", "array-contains", userUid),
      orderBy("timestamp", "desc"),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (querySnapshot) => {
        const roomsMap: Map<string, ChatRoom> = new Map();

        querySnapshot.forEach((doc) => {
          const data = doc.data() as MessageInterface;
          const jobId = data.jobId;
          const companyName = data.companyName || "Unknown Company";

          if (!roomsMap.has(jobId)) {
            roomsMap.set(jobId, {
              jobId,
              sender: data.sender,
              recipient: data.recipient,
              jobTitle: data.jobTitle,
              companyName,
              status: data.status,
              messages: [],
              hasMoreMessages: true,
              lastVisible: null,
              timestamp: data.timestamp,
            });
          }
          const room = roomsMap.get(jobId);
          room?.messages.push(data);
        });

        const sortedRooms = Array.from(roomsMap.values()).map((room) => ({
          ...room,
          messages: room.messages.sort((a, b) => {
            const aTimestamp = a.timestamp 
              ? a.timestamp as Timestamp 
              : Timestamp.fromDate(new Date());
            
            const bTimestamp = b.timestamp 
              ? b.timestamp as Timestamp 
              : Timestamp.fromDate(new Date());
            
            return aTimestamp.toMillis() - bTimestamp.toMillis();
          }),
        }));

        setChatRooms(sortedRooms);
        setLoading(false);
      },
      (err) => {
        console.error("Error in onSnapshot:", err.message);
        setError("Failed to fetch chat rooms.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userUid]);

  return { chatRooms, loading, error };
};

export default useChatRooms;
